/**
 * One machine look per renderer, from the height scale (`spec-onboarding-persistent-scene-model-look`,
 * R3, R6, R7).
 *
 * `MachineLookScope` sits inside a canvas. It owns that renderer's patched copy of the shared
 * `MACHINE_MATERIAL`, the normal-fix uniform every copy of it shares, and the latest `machineLook`.
 * Once a frame it reads the plan view's height scale, and writes the uniform and the copies'
 * metalness; the lights and the occlusion strength read the same look through getters, never React
 * state, so a height change re-renders nothing. Outside a plan view the height reads 1, so the live
 * twin draws the full-height look and follows any later height source with no change here.
 *
 * It also owns one copy per status colour (`spec-onboarding-persistent-scene-status-colour`, R1, R5):
 * `colourFor(hex)` derives the copy once, so it shares the uniform and the program, and the look
 * writes its metalness with the rest. A colour is a uniform, so a status change compiles nothing.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useFrame } from '@react-three/fiber';
import type { MeshStandardMaterial } from 'three';
import {
  LightsAndShadows,
  MACHINE_MATERIAL,
  machineLook,
  patchMachineLook,
  PostProcessChain,
} from '@repo/ui';
import type { MachineLook, PostProcessSwitches } from '@repo/ui';
import { MachineMaterialContext } from '../twinEntities/asset/MachineMaterialContext';
import { usePlanView } from './PlanHeightScale';
import { PLAN_REST_HEIGHT_SCALE } from './planMapLock';

export interface MachineLookValue {
  /** The renderer's own machine material, provided to its machines through `MachineMaterialContext`. */
  material: MeshStandardMaterial;
  /** A further copy (Build floors' faint and selected) that shares the renderer's uniform. */
  derive: () => MeshStandardMaterial;
  /**
   * Has the look written to `copies` from now until the returned function runs. Call it from the
   * effect that owns them: a copy is drawn again after `dispose()` (React's development rehearsal
   * disposes and then reuses them), so its life is the effect's, never its dispose event.
   */
  track: (...copies: MeshStandardMaterial[]) => () => void;
  /**
   * The renderer's machine material in a status colour (`#RRGGBB`): one copy per colour, made on
   * first use, shared by every machine in that colour and disposed with the scope.
   */
  colourFor: (hex: string) => MeshStandardMaterial;
  getLook: () => MachineLook;
  getMix: () => number;
  getOcclusion: () => number;
}

export const MachineLookContext = createContext<MachineLookValue | null>(null);

/** The renderer's machine look, or `null` outside a `MachineLookScope`. */
export const useMachineLook = () => useContext(MachineLookContext);

const createMachineLook = () => {
  // The scope's own, never shared between renderers: two alive at once draw at their own heights.
  const uniform = { value: 1 };
  const material = patchMachineLook(MACHINE_MATERIAL.clone(), uniform);
  const copies = new Set<MeshStandardMaterial>([material]);
  let look = machineLook(1, PLAN_REST_HEIGHT_SCALE);
  const write = () => {
    uniform.value = look.normalScale;
    copies.forEach(copy => {
      copy.metalness = look.metalness;
    });
  };
  write();
  const apply = (heightScale: number) => {
    const next = machineLook(heightScale, PLAN_REST_HEIGHT_SCALE);
    if (next.k === look.k) return;
    look = next;
    write();
  };
  const derive = () => {
    // `clone()` does not carry the patch, so the copy is patched again with this scope's uniform.
    const copy = patchMachineLook(material.clone(), uniform);
    copy.metalness = look.metalness;
    return copy;
  };
  const track = (...tracked: MeshStandardMaterial[]) => {
    tracked.forEach(copy => {
      copy.metalness = look.metalness;
      copies.add(copy);
    });
    return () => tracked.forEach(copy => copies.delete(copy));
  };
  const coloured = new Map<string, MeshStandardMaterial>();
  const colourFor = (hex: string) => {
    const key = hex.toLowerCase();
    let copy = coloured.get(key);
    if (!copy) {
      copy = derive();
      copy.color.set(key);
      copies.add(copy);
      coloured.set(key, copy);
    }
    return copy;
  };
  const dispose = () => {
    material.dispose();
    coloured.forEach(copy => copy.dispose());
  };
  const value: MachineLookValue = {
    material,
    derive,
    track,
    colourFor,
    getLook: () => look,
    getMix: () => look.mix,
    getOcclusion: () => look.occlusion,
  };
  return { value, apply, dispose };
};

export const MachineLookScope = ({ children }: { children: ReactNode }) => {
  const [{ value, apply, dispose }] = useState(createMachineLook);
  const { getHeightScale } = usePlanView();
  // Before every other frame callback (R3F runs them by priority, then in subscription order, and a
  // child subscribes first), so the lights and the occlusion read this frame's look. Negative, so it
  // does not take over rendering.
  useFrame(() => apply(getHeightScale()), -1);
  // Disposed with the renderer, the colour copies too; a renderer still drawing one would upload it
  // again (React's development rehearsal disposes and then reuses them).
  useEffect(() => dispose, [dispose]);
  return (
    <MachineLookContext.Provider value={value}>
      <MachineMaterialContext.Provider value={value.material}>
        {children}
      </MachineMaterialContext.Provider>
    </MachineLookContext.Provider>
  );
};

/** Copies outside any scope (tests, and a plan drawn with no look): one per colour, never disposed. */
const unscopedColours = new Map<string, MeshStandardMaterial>();
const unscopedColourFor = (hex: string) => {
  const key = hex.toLowerCase();
  let copy = unscopedColours.get(key);
  if (!copy) {
    copy = MACHINE_MATERIAL.clone();
    copy.color.set(key);
    unscopedColours.set(key, copy);
  }
  return copy;
};

/**
 * The machine material in a status colour: the renderer's scope's copy, or outside any scope a clone
 * of the shared `MACHINE_MATERIAL` (as `makeBuildFloorsMaterials` falls back), at most one per colour.
 */
export const useMachineColour = (): ((hex: string) => MeshStandardMaterial) => {
  const look = useMachineLook();
  const colourFor = look?.colourFor;
  return useCallback((hex: string) => (colourFor ?? unscopedColourFor)(hex), [colourFor]);
};

/** The scene's lights, crossfaded by the renderer's look; today's lighting outside a scope. */
export const MachineLookLights = () => {
  const look = useMachineLook();
  return <LightsAndShadows getMix={look?.getMix} />;
};

/**
 * The twin's post-processing, its occlusion strength from the renderer's look. `stencil` gives its
 * composer a stencil buffer (the 3D plan's, for `plan-machine-rotate-ring`); the live twin has none.
 */
export const MachineLookPostProcessChain = ({
  effects,
  stencil = false,
}: {
  effects?: PostProcessSwitches;
  stencil?: boolean;
}) => {
  const look = useMachineLook();
  return <PostProcessChain effects={effects} getOcclusion={look?.getOcclusion} stencil={stencil} />;
};
